import { type AlertProps, AlertState, AlertVariant, stateConfig, variantStyles } from './types';
import { cn } from '../../utils/cn';

/**
 * A styled alert component for displaying contextual feedback messages.
 *
 * @param state - The alert state that determines colors and icon. Defaults to {@link AlertState.INFO}.
 * @param variant - Visual style variant. Defaults to {@link AlertVariant.PRIMARY}.
 * @param title - Optional bold title displayed above the description.
 * @param children - Alert description/content.
 * @param hideIcon - Whether to hide the state icon. Defaults to `false`.
 * @param className - Additional CSS classes to merge with default styles.
 *
 * @example
 * <Alert state={AlertState.SUCCESS} title="Success">
 *   Your changes have been saved.
 * </Alert>
 *
 * @example
 * <Alert state={AlertState.ERROR} variant={AlertVariant.SECONDARY} title="Error">
 *   Something went wrong. Please try again.
 * </Alert>
 *
 * @example
 * <Alert state={AlertState.WARNING}>
 *   Your session will expire in 5 minutes.
 * </Alert>
 *
 * @see {@link AlertState} for all available states.
 * @see {@link AlertVariant} for all available variants.
 */
export const Alert: React.FC<AlertProps> = ({
    children,
    className,
    hideIcon = false,
    state = AlertState.INFO,
    title,
    variant = AlertVariant.PRIMARY,
    ...props
}) => {
    const { bg, border, icon: Icon, iconColor } = stateConfig[state];

    return (
        <div
            className={cn(
                'flex items-start gap-3 px-3 py-2 rounded',
                'border-solid',
                bg,
                border,
                variantStyles[variant],
                className,
            )}
            role="alert"
            {...props}
        >
            {!hideIcon && <Icon className={cn('shrink-0 size-4 mt-0.5 self-center', iconColor)} aria-hidden="true" />}
            <div className="flex flex-col text-sm text-midnight">
                {title && <span className="font-bold">{title}</span>}
                {children && <span className="font-normal">{children}</span>}
            </div>
        </div>
    );
};
